import ImageComponent from '@components/common/ImageComponent/ImageComponent';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { Trend, TrendGroup } from '@lib/ProAPI/pro-api.types';
import React, { Fragment, useContext, useMemo, useState } from 'react';
import Slider from '@components/slider/Index';
import NextButton from '@components/slider/NextButton';
import PrevButton from '@components/slider/PrevButton';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import { RedirectContext, StateContext } from '@store/store';
import Image from 'next/image';

const TrendCard = ({
  trend,
  isMuted,
  toggleMute,
}: {
  trend: Trend;
  isMuted: boolean;
  toggleMute: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const [isHovered, _setIsHovered] = useState(true);
  const { goToAiSignup } = useContext(RedirectContext);

  const { platform } = useContext(StateContext);

  const assetsUrl = process.env.NEXT_PUBLIC_EDITOR_CDN_URL;

  const isDesktop = platform === PlatformType.Desktop;

  const handleTrendClick = () => {
    goToAiSignup({
      ux_source: 'Trends',
      nextUrl: `/agents-models?trend=${trend.id}`,
    });
  };

  return (
    <div className={ivclass('iv-w-[168px] iv-h-[278px] iv-flex-shrink-0')}>
      <div
        key={trend.id}
        className={ivclass(
          'iv-flex iv-flex-col iv-gap-12 iv-w-[168px] iv-h-[278px]',
          'iv-relative',
        )}
        onClick={handleTrendClick}
        // onMouseEnter={() => setIsHovered(true)}
        // onMouseLeave={() => setIsHovered(false)}
        // onFocus={() => setIsHovered(true)}
        // onBlur={() => setIsHovered(false)}
      >
        {isDesktop && isHovered ? (
          <button
            onClick={toggleMute}
            className={ivclass(
              'iv-absolute iv-top-[10px] iv-left-[10px]',
              'iv-opacity-30 hover:iv-opacity-100',
              'iv-bg-grey-0',
              'iv-border-solid iv-border-grey-85',
              'iv-rounded-full',
              'iv-w-[25px] iv-h-[25px]',
              'iv-flex iv-items-center iv-justify-center',
              'iv-shadow-md',
              'iv-cursor-pointer',
              'iv-transition-all iv-duration-500 iv-ease-in-out',
              'hover:iv-scale-100',
            )}
            style={{
              border: '5px solid rgba(133, 133, 133, 0.2)',
              zIndex: 100,
            }}
            onMouseOver={e => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.opacity = '0.3';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {isMuted ? (
              <Image
                width={20}
                height={20}
                src={
                  assetsUrl +
                  '/dark/panels/top-panel/component-props/selected/volume-mute.svg'
                }
                alt="Mute"
              />
            ) : (
              <Image
                width={20}
                height={20}
                src={
                  assetsUrl +
                  '/dark/panels/top-panel/component-props/selected/volume.svg'
                }
                alt="Unmute"
              />
            )}
          </button>
        ) : null}
        {trend.preview_url && isHovered ? (
          <video
            src={trend.preview_url}
            poster={trend.thumbnail_url}
            className={ivclass(
              'iv-w-full iv-h-full iv-min-h-[240px] iv-rounded-16 iv-object-cover iv-border iv-border-grey-10',
            )}
            autoPlay
            muted={isMuted}
            loop
            playsInline
          />
        ) : (
          <ImageComponent
            elementype="native"
            src={trend.thumbnail_url}
            alt={trend.display_name}
            className={ivclass(
              'iv-w-full iv-h-full iv-min-h-[240px] iv-rounded-16 iv-object-cover',
            )}
          />
        )}
        <span
          className={ivclass('iv-text-body-xl iv-text-left iv-text-grey-95')}
        >
          {trend.display_name}
        </span>
      </div>
    </div>
  );
};

function VideoWallTrendsTray({
  trendGroups,
  platform,
}: {
  trendGroups: TrendGroup[];
  platform: PlatformType;
}) {
  const trends = useMemo(() => {
    if (!trendGroups) return [];

    return trendGroups.flatMap(group =>
      group.trends_families
        .flatMap(family => family.trends)
        .sort((a, b) => a.index - b.index),
    );
  }, [trendGroups]);

  const [isMuted, setIsMuted] = useState(true);

  const toggleMute = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsMuted(!isMuted);
  };

  return (
    <div
      className={ivclass(
        'iv-flex sm:iv-justify-center iv-flex-col',
        'iv-py-20 iv-px-60 md:iv-px-25 sm:iv-px-35',
        'iv-border-b iv-border-t iv-border-grey-5',
      )}
    >
      <div className={ivclass('iv-flex iv-justify-between')}>
        <h2
          className={ivclass(
            'iv-m-heading-3xs iv-text-heading-3xs md:iv-m-heading-3xs md:iv-text-heading-3xs sm:iv-text-heading-4xs',
            'iv-font-extrabold iv-text-grey-95',
            'iv-mb-10 iv-text-left sm:iv-text-center',
          )}
        >
          Trends
        </h2>
        <div className={ivclass('iv-flex iv-gap-x-10')}>
          <a
            href={`/trends/${trendGroups[0].id}`}
            className={ivclass(
              'iv-border iv-border-grey-70 iv-rounded-25 iv-flex iv-items-center iv-justify-center iv-w-16',
              'iv-text-body-l iv-text-grey-95 iv-font-medium iv-px-4',
            )}
          >
            See all
          </a>
        </div>
      </div>
      <div className={ivclass('iv-overflow-hidden iv-py-20')}>
        <Slider
          sliderWrapperClasses={ivclass(
            'iv-relative iv-w-full',
            'md:iv-ml-20 sm:iv-ml-20',
          )}
          childrenWrapperClass={ivclass('iv-gap-20')}
          align="start"
          renderButtons={(
            scrollPrev,
            scrollNext,
            isPrevDisabled,
            isNextDisabled,
          ) => {
            if (
              platform === PlatformType.Mobile ||
              platform === PlatformType.Tablet
            ) {
              return <Fragment />;
            }

            return (
              <Fragment>
                <PrevButton
                  clickHandler={scrollPrev}
                  isEndReached={isPrevDisabled}
                />
                <NextButton
                  clickHandler={scrollNext}
                  isEndReached={isNextDisabled}
                />
              </Fragment>
            );
          }}
        >
          {trends.map(trend => (
            <TrendCard
              trend={trend}
              key={trend.id}
              isMuted={isMuted}
              toggleMute={toggleMute}
            />
          ))}
        </Slider>
      </div>
    </div>
  );
}

export default VideoWallTrendsTray;
